import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './views/Home';
import ProjectsCatalog from './views/ProjectsCatalog';
import ProjectDetail from './views/ProjectDetail';
import TeamShowcase from './views/TeamShowcase';
import AdminDashboard from './admin/AdminDashboard';

export default function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [queryParams, setQueryParams] = useState(new URLSearchParams(window.location.search));
  const [currentHash, setCurrentHash] = useState(window.location.hash);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
      setQueryParams(new URLSearchParams(window.location.search));
      setCurrentHash(window.location.hash);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (currentHash) {
      requestAnimationFrame(() => {
        document.getElementById(decodeURIComponent(currentHash.slice(1)))?.scrollIntoView({ behavior: 'smooth' });
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [currentPath, currentHash]);

  const navigate = (path) => {
    const previousPath = window.location.pathname;
    const previousHash = window.location.hash;
    window.history.pushState({}, '', path);
    setCurrentPath(window.location.pathname);
    setQueryParams(new URLSearchParams(window.location.search));
    setCurrentHash(window.location.hash);
    if (previousPath === window.location.pathname && previousHash === window.location.hash && window.location.hash) {
      requestAnimationFrame(() => {
        document.getElementById(decodeURIComponent(window.location.hash.slice(1)))?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  };

  const { data: auth, isPending: authLoading } = useQuery({
    queryKey: ['auth-me'],
    queryFn: async () => {
      const res = await fetch('/api/auth/me', { credentials: 'include', cache: 'no-store' });
      if (!res.ok) throw new Error('Unable to check Cloudflare Access session');
      return res.json();
    },
    retry: false,
  });

  const handleLogout = async () => {
    queryClient.setQueryData(['auth-me'], { authenticated: false });
    // Cloudflare Access owns CF_Authorization; the app cannot clear or revoke
    // that token itself. Its logout endpoint clears the Access session.
    try {
      await fetch('/cdn-cgi/access/logout', {
        credentials: 'include',
        cache: 'no-store',
        redirect: 'manual',
      });
    } finally {
      window.location.replace('/');
    }
  };

  // Router matching
  let view = null;
  if (currentPath === '/' || currentPath === '') {
    view = <Home onNavigate={navigate} initialSector={queryParams.get('sector') || 'all'} />;
  } else if (currentPath === '/projects') {
    const sector = queryParams.get('sector') || 'all';
    view = <ProjectsCatalog onNavigate={navigate} initialSector={sector} />;
  } else if (currentPath.startsWith('/projects/')) {
    const slug = currentPath.split('/')[2];
    view = <ProjectDetail slug={slug} preview={queryParams.get('preview') === '1'} onNavigate={navigate} />;
  } else if (currentPath === '/team') {
    view = <TeamShowcase />;
  } else if (currentPath === '/admin') {
    if (authLoading) {
      view = <div className="page-state"><p>Verificando acesso…</p></div>;
    } else if (!auth?.authenticated) {
      view = (
        <div className="page-state">
          <h2 className="font-display page-state__title">Acesso restrito</h2>
          <p>Entre pelo Cloudflare Access para abrir o painel administrativo.</p>
          <a href="/admin" className="btn btn--solid page-state__action">Tentar novamente</a>
        </div>
      );
    } else {
      view = <AdminDashboard onLogout={handleLogout} onNavigate={navigate} />;
    }

  } else {
    view = (
      <div className="page-state">
        <h2 className="font-display page-state__title">404 — Página não encontrada</h2>
        <button onClick={() => navigate('/')} className="btn btn--solid page-state__action">
          Voltar ao Início
        </button>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {currentPath !== '/admin' && <Navbar currentPath={currentPath} onNavigate={navigate} showAdminLink={Boolean(auth?.authenticated)} />}
      <main className="app-shell__main">
        {view}
      </main>
      {currentPath !== '/admin' && <Footer onNavigate={navigate} />}
    </div>
  );
}
